// Tipos comunes del wire format: ids, coordinates, fechas, instantes, enums
// compartidos y los ladrillos numericos. La API es multi-tenant (ADR-001):
// toda operacion ocurre en el contexto de una site, identificada por SiteId.
//
// POLITICA DE AUSENCIA (vale para todo el paquete; la app Kotlin se genera de
// aca, ADR-002):
// - `.optional()` = el field PUEDE NO VENIR en el JSON. Ausente significa
//   "sin dato" y el cliente lo trata como tal. Es el default para todo lo que
//   no siempre existe (demora, driver, cursor de paginacion).
// - `.nullable()` SOLO cuando null tiene un significado propio, distinto de
//   "sin dato": "nunca hubo" frente a "todavia no", o "no calculable" en una
//   metrica. Cada `.nullable()` lo explica en su description.
// - Nunca `.nullish()` en el wire: un field es una cosa o la otra.
//
// NUMEROS: todo entero lleva `format: int32` (Integer) y todo decimal
// `format: double` (Decimal), para que el generador Kotlin elija Int y Double
// sin adivinar. Se usan como ladrillos: `Integer.min(0)`, `Decimal.min(0)`.
import { z } from "zod";
import { extendZodWithOpenApi } from "@asteasolutions/zod-to-openapi";
// Solo ZodType: es lo unico que lee extendZodWithOpenApi (zod-to-openapi 9).
// Con el namespace entero como valor, Rolldown (Vite 8) lo materializa y las
// SPAs cargan todo zod con sus locales (unos 50 kB gzip mas); Rollup lo sacudia.
extendZodWithOpenApi({ ZodType: z.ZodType });
// zod 4 compila los parsers de objetos con new Function y antes lo prueba una
// vez; bajo la CSP de las SPAs (sin unsafe-eval) esa prueba ya cuenta como
// violacion aunque falle en silencio. Sin JIT parsea igual, como zod 3.
z.config({ jitless: true });
/**
 * `.strict()` para el `.extend()` de un schema con nombre. zod-to-openapi 9 no
 * pasa su metadata por `.strict()` y el schema saldria inline en vez de allOf
 * con $ref a la base; en zod 4 strict es exactamente catchall(never), que si
 * la conserva. El tipo es el de `.strict()`, sin index signature.
 */
export function strictExtension(schema) {
    return schema.catchall(z.never());
}
/** Integer de 32 bits. Ladrillo, no un tipo del wire: no se registra. */
export const Integer = z.number().int().openapi({ format: "int32" });
/** Decimal de doble precision. Ladrillo, no un tipo del wire: no se registra. */
export const Decimal = z.number().openapi({ format: "double" });
export const SiteId = z.string().min(1).openapi("SiteId", {
    description: "Identificador de la planta industrial (tenant). La API es multi-tenant (ADR-001): todo recurso pertenece a una planta y el encuadre por tenant es obligatorio.",
    example: "site_pbb",
});
export const RecordId = z.string().min(1).openapi("RecordId", {
    description: "Identificador del legajo del empleado dentro de su planta (lenguaje ubicuo del cliente).",
    example: "record_10482",
});
export const ProviderId = z.string().min(1).openapi("ProviderId", {
    description: "Identificador de la empresa transportista. En el modelo relevado hay un unico proveedor de combis por planta, pero el contrato admite N.",
    example: "prov_shuttles_bb",
});
export const TripId = z.string().min(1).openapi("TripId", {
    description: "Identificador de un viaje (recorrido concreto de un vehiculo).",
    example: "trip_20260908_0630_c01",
});
export const RideRequestId = z.string().min(1).openapi("RideRequestId", {
    description: "Identificador de una solicitud de transporte de un pasajero.",
    example: "sol_000123",
});
export const StopId = z.string().min(1).openapi("StopId", {
    description: "Identificador de una parada (punto de ascenso o descenso).",
    example: "stop_alem_1200",
});
export const Coordinates = z
    .object({
    lat: Decimal.min(-90).max(90).openapi({
        description: "Latitud en grados decimales (WGS84), entre -90 y 90.",
        example: -38.7183,
    }),
    lng: Decimal.min(-180).max(180).openapi({
        description: "Longitud en grados decimales (WGS84), entre -180 y 180.",
        example: -62.2663,
    }),
})
    .openapi("Coordinates", { description: "Par de coordenadas geograficas (WGS84)." });
export const Instant = z.string().datetime().openapi("Instant", {
    description: "Instant en formato ISO 8601 en UTC (sufijo Z). Toda fecha-hora del wire format viaja en UTC; la conversion a hora local es responsabilidad del cliente.",
    example: "2026-09-08T09:30:00Z",
});
// Unica definicion de fecha calendario del paquete (antes habia tres).
export const CalendarDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "fecha en formato AAAA-MM-DD")
    .openapi("CalendarDate", {
    description: "Fecha calendario sin hora, en formato ISO AAAA-MM-DD, interpretada en la zona horaria de la planta (nunca en UTC).",
    example: "2026-09-15",
});
// Unica definicion de hora del dia del paquete (shifts, pasadas de bus).
export const LocalTime = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "hora en formato HH:MM de 24 horas")
    .openapi("LocalTime", {
    description: "Hora del dia en formato HH:MM de 24 horas, en la hora local de la planta.",
    example: "06:00",
});
export const VehicleType = z.enum(["shuttle", "cab", "bus"]).openapi("VehicleType", {
    description: "Tipo de vehiculo del servicio: shuttle (transporte principal), cab (excepciones y casos puntuales) o bus (recorrido fijo con reserva).",
});
// --- Parametros de path compartidos ------------------------------------------
export const SiteParams = z
    .object({ siteId: SiteId })
    .openapi("SiteParams", {
    description: "Parametro de path :siteId. La API SIEMPRE lo compara contra el tenant del token: no es una fuente de tenant por si mismo.",
});
//# sourceMappingURL=common.js.map